/**
 * Structured logging for CobrowsingUI Recorder.
 *
 * Provides level-based logging (debug, info, warn, error) with structured
 * output for easier debugging and log aggregation.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

export interface LogEntry {
  timestamp: string;
  level: string;
  context: string;
  message: string;
  data?: unknown;
}

type LogCallback = (entry: LogEntry) => void;

let currentLevel: LogLevel = LogLevel.INFO;
let logCallback: LogCallback | null = null;
let sampleRate = 1.0; // 100% by default

/**
 * Sets the minimum log level.
 * Messages below this level will be suppressed.
 */
export function setLogLevel(level: LogLevel): void {
  currentLevel = level;
}

/**
 * Gets the current log level.
 */
export function getLogLevel(): LogLevel {
  return currentLevel;
}

/**
 * Sets log sampling rate (0.0 to 1.0).
 * Use lower values in production to reduce log volume.
 */
export function setSampleRate(rate: number): void {
  sampleRate = Math.max(0, Math.min(1, rate));
}

/**
 * Gets current sample rate.
 */
export function getSampleRate(): number {
  return sampleRate;
}

/**
 * Sets a callback for log entries.
 * Useful for sending logs to external services.
 */
export function setLogCallback(callback: LogCallback | null): void {
  logCallback = callback;
}

/**
 * Creates a logger instance for a specific context.
 */
export function createLogger(context: string) {
  const log = (level: LogLevel, message: string, data?: unknown) => {
    if (level < currentLevel) return;

    // Apply sampling for info level (not warn/error)
    if (level === LogLevel.INFO && sampleRate < 1.0) {
      if (Math.random() > sampleRate) return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      context,
      message,
      data,
    };

    // Output to console with appropriate method
    const consoleMsg = `[${entry.timestamp}] [${entry.level}] [${context}] ${message}`;
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(consoleMsg, data ?? '');
        break;
      case LogLevel.INFO:
        console.log(consoleMsg, data ?? '');
        break;
      case LogLevel.WARN:
        console.warn(consoleMsg, data ?? '');
        break;
      case LogLevel.ERROR:
        console.error(consoleMsg, data ?? '');
        break;
    }

    // Call callback if set
    if (logCallback) {
      logCallback(entry);
    }
  };

  return {
    debug: (message: string, data?: unknown) => log(LogLevel.DEBUG, message, data),
    info: (message: string, data?: unknown) => log(LogLevel.INFO, message, data),
    warn: (message: string, data?: unknown) => log(LogLevel.WARN, message, data),
    error: (message: string, data?: unknown) => log(LogLevel.ERROR, message, data),
  };
}

// Default logger for general use
export const logger = createLogger('CobrowsingUI');

// Convenience function to enable debug logging
export function enableDebug(): void {
  setLogLevel(LogLevel.DEBUG);
}

// Convenience function to disable all logging
export function disableLogging(): void {
  setLogLevel(LogLevel.NONE);
}
